import React, {
  useCallback,
  useImperativeHandle,
  forwardRef,
  useState,
} from 'react'
import { isEqual } from 'lodash'

import { Text } from '@wartek-id/text-v1'
import { Link } from '@wartek-id/link'
import { Button } from '@wartek-id/button-v1'

import CardIndikatorDimensi from 'app/KinerjaPage/components/CardIndikatorDimensi'
import ExpanderCardInline from 'app/KinerjaPage/components/ExpanderCardInline'
import DialogDimensiDetail from './components/DialogDimensiDetail'
import useContentContext from 'app/KinerjaPage/hooks/useContentContext'
import { useAPIKinerjaProfile } from 'api/kinerja/useAPIKinerja'
import { PraktikPembelajarIcon } from './components/PraktikPembelajarIcon'
import StickyFooter from 'app/KinerjaPage/components/StickyFooter'
import useSKP from 'app/KinerjaPage/hooks/useSKP'
import AdjustmentInfo from 'app/KinerjaPage/components/AdjustmentInfo'
import {
  skpStatusPerencanaanFinal,
  skpStatusWaitingApproval,
} from 'app/KinerjaPage/constants'

import type { BadgeColor } from 'app/KinerjaPage/components/Badge'
import type {
  KinerjaPerencanaanTabBaseProps,
  KinerjaPraktikPembelajaranContentIndikator,
  SKPTabHandler,
  UpdateSKPPayload,
} from 'types/kinerja'
import type { ForwardedRef } from 'react'

interface PerencanaanTabTugasPokokProps extends KinerjaPerencanaanTabBaseProps {
  customHeader?: () => JSX.Element
  hideFooter?: boolean
}

type ExpanderCardInlineProps = React.ComponentProps<typeof ExpanderCardInline>
type DivProps = React.ComponentProps<'div'>

type IndicatorsWrapperProps = {
  shouldHideOtherIndicators: boolean
} & ExpanderCardInlineProps &
  DivProps

function IndicatorsWrapper({
  shouldHideOtherIndicators = false,
  children,
  openText,
  closeText,
  className,
  open,
  ...props
}: IndicatorsWrapperProps) {
  if (shouldHideOtherIndicators) {
    return (
      <ExpanderCardInline
        openText={openText}
        closeText={closeText}
        open={open}
        className={className}
      >
        {children}
      </ExpanderCardInline>
    )
  } else {
    return (
      <div className={className} {...(props as DivProps)}>
        {children}
      </div>
    )
  }
}

const TabPraktikPembelajaran = (
  {
    skp,
    isSubmitting = false,
    reviewMode = false,
    hideFooter = false,
    onClickNext = () => {},
    onClickSave = () => {},
    customHeader,
  }: PerencanaanTabTugasPokokProps,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const { getChangesPraktikPembelajaran } = useSKP(skp)
  const { contentPraktikPembelajaran, contentKinerjaUtama, isRaporExists } =
    useContentContext()

  const { data: profile, isFetched: isProfileFetched } = useAPIKinerjaProfile()

  const dimensions = contentPraktikPembelajaran.sort((a, b) => a.skor - b.skor)

  const isRecommendedDimension = ({ skor }) => skor === 1

  const recommendedDimension = isRaporExists
    ? dimensions.filter(isRecommendedDimension)
    : []
  const otherDimension = isRaporExists
    ? dimensions.filter((item) => !isRecommendedDimension(item))
    : dimensions

  const isWaitingApproval =
    skpStatusWaitingApproval.includes(skp.status) && !reviewMode

  const mapColor = (skor): BadgeColor => {
    switch (skor) {
      case 1:
        return 'critical'
      case 2:
        return 'warning'
      case 3:
        return 'success'
      default:
        return 'default'
    }
  }

  const [selectedIndicator, setSelectedIndicator] =
    useState<KinerjaPraktikPembelajaranContentIndikator | null>(
      dimensions
        .flatMap((dimensi) => dimensi.indikator)
        .find(
          (indikator) =>
            indikator.id ===
            skp[
              reviewMode ? 'reviewPraktikPembelajaran' : 'praktikPembelajaran'
            ]
        ) ?? null
    )

  const { originalKinerja: originalPraktikPembelajaran } =
    getChangesPraktikPembelajaran()

  const showAdjustmentInfo =
    reviewMode && !isEqual(originalPraktikPembelajaran, selectedIndicator)
  const isDisabledNextButton = selectedIndicator === null

  const handleSelected = (
    selected: KinerjaPraktikPembelajaranContentIndikator
  ) => {
    setSelectedIndicator(selected)
  }

  const [showRaporPendidikanDialog, setShowRaporPendidikanDialog] =
    useState(false)

  const generatePayload = useCallback((): UpdateSKPPayload => {
    const kinerjaUtama = contentKinerjaUtama.map((kinerja) => kinerja.id)
    const praktikPembelajaran: number = selectedIndicator?.id ?? 0

    return {
      id: skp.id,
      kinerjaUtama,
      praktikPembelajaran,
    }
  }, [contentKinerjaUtama, selectedIndicator, skp])

  const onSaveDraft = useCallback(() => {
    onClickSave(generatePayload())
  }, [onClickSave, generatePayload])

  const onSubmit = () => {
    onClickNext(generatePayload())
  }

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: onSaveDraft,
    }),
    [onSaveDraft]
  )

  return (
    <div>
      <div className="p-9 overflow-auto">
        {customHeader && <div className="mb-9">{customHeader()}</div>}

        <div className="max-w-[954px] flex flex-col gap-3">
          {showAdjustmentInfo && (
            <AdjustmentInfo
              originalKinerjaNumber={null}
              originalKinerja={originalPraktikPembelajaran}
              kinerjaNumber={null}
              kinerja={selectedIndicator}
              showDetails={false}
              label=""
              className="mb-3"
            />
          )}

          {!reviewMode &&
            !isWaitingApproval &&
            !skpStatusPerencanaanFinal.includes(skp.status) && (
              <div>
                <Text variant="heading-md">
                  Pilih 1 indikator untuk menjadi fokus peningkatan kinerja Anda
                </Text>
                {isRaporExists ? (
                  <Text className="mt-1" color="subdued">
                    Kami sediakan rekomendasi berdasarkan
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        setShowRaporPendidikanDialog(true)
                      }}
                      className="font-bold"
                    >
                      Rapor Pendidikan satuan pendidikan Anda
                    </Link>
                    versi terakhir.
                  </Text>
                ) : (
                  <Text className="mt-1" color="subdued">
                    Diskusikan dengan atasan agar pilihan Anda turut mendukung
                    peningkatan kinerja Satdik.
                  </Text>
                )}
              </div>
            )}

          {isRaporExists && (
            <div className="max-w-[620px] px-4 py-2 flex items-center bg-[#FAFAFA] rounded- rounded-[4px]">
              <div>
                <Text variant="action">Kualitas Pembelajaran</Text>
              </div>
              <div className="border-r border-gray-20 mx-4">&nbsp;</div>
              <div className="w-[456px] flex flex-col">
                <Text color="subdued">Dimensi Prioritas:</Text>

                <Text variant="action" color="subdued">
                  {contentPraktikPembelajaran
                    .filter(isRecommendedDimension)
                    .sort((a, b) => a.dimensiId.localeCompare(b.dimensiId))
                    .map((e) => e.dimensiNama)
                    .join(', ') || 'Tidak ada. Pilih sesuai kebutuhan Anda.'}
                </Text>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {isRaporExists &&
            recommendedDimension.map((dimensi) => (
              <div
                key={`dimensi-${dimensi.dimensiId}`}
                className="grid grid-cols-3 gap-x-6 gap-y-3"
              >
                {dimensi.indikator.map((indikator) => (
                  <CardIndikatorDimensi
                    key={`recommended-indikator-${indikator.id}`}
                    data-testid={`recommended-indikator-${indikator.id}`}
                    dimension={dimensi.dimensiNama}
                    icon={
                      <PraktikPembelajarIcon subindicator={indikator.nama} />
                    }
                    recommended
                    color="critical"
                    indikator={indikator}
                    isDisabled={
                      skpStatusPerencanaanFinal.includes(skp.status) ||
                      isWaitingApproval
                    }
                    isSelected={selectedIndicator?.id === indikator.id}
                    onSelected={(selected) => {
                      if (selected) {
                        handleSelected(indikator)
                      } else {
                        handleSelected(null)
                      }
                    }}
                  />
                ))}
              </div>
            ))}

          {/* Others. Hideable */}
          {otherDimension.length > 0 && (
            <IndicatorsWrapper
              shouldHideOtherIndicators={
                isRaporExists && recommendedDimension.length > 0
              }
              openText="Tampilkan indikator lain"
              closeText="Sembunyikan indikator lain"
              className="flex flex-col gap-y-3"
            >
              {otherDimension.map((dimensi) => (
                <div
                  key={dimensi.dimensiId}
                  className="grid grid-cols-3 gap-x-6 gap-y-3"
                >
                  {dimensi.indikator.map((indikator) => (
                    <CardIndikatorDimensi
                      key={`other-indikator-${indikator.id}`}
                      data-testid={`other-indikator-${indikator.id}`}
                      dimension={dimensi.dimensiNama}
                      icon={
                        <PraktikPembelajarIcon subindicator={indikator.nama} />
                      }
                      color={isRaporExists ? mapColor(dimensi.skor) : 'default'}
                      indikator={indikator}
                      isDisabled={
                        skpStatusPerencanaanFinal.includes(skp.status) ||
                        isWaitingApproval
                      }
                      isSelected={selectedIndicator?.id === indikator.id}
                      onSelected={(selected) => {
                        if (selected) {
                          handleSelected(indikator)
                        } else {
                          handleSelected(null)
                        }
                      }}
                    />
                  ))}
                </div>
              ))}
            </IndicatorsWrapper>
          )}
        </div>
      </div>

      {!hideFooter && (
        <StickyFooter>
          {!skpStatusPerencanaanFinal.includes(skp.status) &&
            !isWaitingApproval && (
              <Button
                size="lg"
                color="white"
                isLoading={isSubmitting}
                disabled={isDisabledNextButton || isSubmitting}
                onClick={onSaveDraft}
                data-testid="btn-save-draft"
              >
                Simpan draf
              </Button>
            )}
          <Button
            size="lg"
            color="black"
            isLoading={isSubmitting}
            disabled={isDisabledNextButton || isSubmitting}
            type="submit"
            data-testid="btn-next"
            onClick={onSubmit}
          >
            Ke Pengembangan Kompetensi
          </Button>
        </StickyFooter>
      )}

      {isProfileFetched && (
        <DialogDimensiDetail
          data-testid="dialog-dimensi-detail"
          size="lg"
          contentPraktikPembelajaran={contentPraktikPembelajaran}
          isOpen={showRaporPendidikanDialog}
          onClose={() => setShowRaporPendidikanDialog(false)}
          profile={profile.data}
        />
      )}
    </div>
  )
}

export default forwardRef<SKPTabHandler, PerencanaanTabTugasPokokProps>(
  TabPraktikPembelajaran
)
